import { ErrorSharp } from "@material-ui/icons";
import moment from "moment";
import { FC, useEffect, useState, useCallback } from "react";
import Cards from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import { DEAFULT_CARTAO } from "src/constants";
import { CollectionUtil } from "src/framework/commons";
import { TypeMask } from "src/framework/commons/masks";
import { FullWidthButton } from "src/framework/components/buttons";
import { InputComboBoxList, InputMoney, InputText } from "src/framework/components/forms";
import { InputDateMonthYear } from "src/framework/components/forms/inputs/InputDateMonthYear";
import { STATUS_CODE } from "src/framework/providers/api";
import pagamentoProvider, { IGetParcelas } from "src/providers/pagamentos/PagamentosProvider";
import { CartaoCreditoParcelaType, CartaoCreditoType, PagamentoCartaoType } from "src/types";
import { Alert } from "@material-ui/lab";

import "./styles.scss";

interface ChangeValue {
    isValid: boolean;
    newValue: any;
}

interface ErrorCartao {
    [name: string]: string | null
}

interface CartaoCreditoProperties {
    pagamentoCartao?: CartaoCreditoType & PagamentoCartaoType;
    onChange: (cartao: CartaoCreditoType & PagamentoCartaoType) => void;
    entrada: boolean;
    onConfirmar: () => void;
    disabled?: boolean;
    disabledValoPargamento?: boolean;
    onChangeValue?: (changeValue: ChangeValue) => void;
    solo?: boolean;
    qtdMaxParcelas?: number;
    valorReferencia?: number;
}

const CartaoCredito: FC<CartaoCreditoProperties> = ({
    pagamentoCartao,
    onChange,
    onConfirmar,
    onChangeValue,
    entrada,
    disabledValoPargamento = false,
    disabled = false,
    solo = true,
    qtdMaxParcelas,
    valorReferencia
}) => {
    const [state, setState] = useState<CartaoCreditoType & PagamentoCartaoType>(DEAFULT_CARTAO)
    const [parcelaState, setParcelaState] = useState<CartaoCreditoParcelaType | null>(null)
    const [valid, setValid] = useState<boolean>(false)
    const [disableParcelas, setDisableParcelas] = useState<boolean>(false)
    const [parcelasListState, setParcelasListState] = useState<CartaoCreditoParcelaType[]>([]);
    const [consultaParcela, setConsultaParcela] = useState<IGetParcelas | null>(null);
    const [pagamentoValue, setPagamentoValue] = useState<number>(-1);
    const [errors, setErrors] = useState<ErrorCartao>({});
    const [cvvMask, setCvvMask] = useState<TypeMask>("credit_card_cvc");

    const getMaxDate = () => {
        /*
        const now = moment().add(10, "years")
        return now.toDate();
        */
        const now = new Date();
        now.setFullYear(now.getFullYear() + 10);

        return now;
    }

    const getMinDate = () => {
        const now = moment()
        return new Date(now.format("yyyy-MM-01"))
    }

    const hasError = (fieldName: string, errorList: ErrorCartao): {hasError: boolean, message: string | null} => {
        if (!!errorList[fieldName]) {
            return {
                hasError: true,
                message: errorList[fieldName]
            }
        }

        return {
            hasError: false,
            message: ""
        };
    }

    const parseDate = (date: string | undefined | null): Date | null => {
        let parsedDate: string | null = null
        if (date) {
            let mes = date.split("/")[0]
            parsedDate = `${date.split("/")[1]}-${Number(mes)}-01`
        }

        return date ? new Date(Number(date.split("/")[1]), Number(date.split("/")[0]) - 1, 1) : null;
    }

    const handleInputFocus = (e: any) => {
        setState({ ...state, focus: e.target.name });
    }

    const handleInputChange = (e: any) => {
        const newState = { ...state }
        const { name, value } = e?.target || e;

        if (name === "number") {
            if (String(value) !== String(state.number)) {
                newState.parcela = null;
                setParcelaState(null);
            }
        }

        if (name === "cvc") {
            if (String(value).length > 3 && !["amex", "discover"].includes(newState.issuer)) {
                return;
            }

            if (String(value).length > 4 && ["amex", "discover"].includes(newState.issuer)) {
                return;
            }
        }
        (newState as any)[name] = value;


        const _isValid = isValid();
        if (!solo && onChangeValue) {
            onChangeValue({
                newValue: newState,
                isValid: _isValid
            })
        }

        newState.valor_original = pagamentoValue
        setState(newState);
        onChange(newState);
        setValid(_isValid);
    }

    useEffect(() => {
        (async () => {
            if (pagamentoCartao) {
                setState(pagamentoCartao);
                if (consultaParcela) {
                    consultaParcela.codigo_venda = String(pagamentoCartao.codigo_venda)
                    consultaParcela.bandeira = pagamentoCartao.issuer
                    setConsultaParcela(consultaParcela)

                    if (pagamentoCartao?.parcela && parcelaState === null) {
                        const newState: CartaoCreditoType & PagamentoCartaoType = { ...state };
                        newState.valor_parcela = pagamentoCartao?.parcela.valor;
                        newState.parcelas = pagamentoCartao?.parcela.parcelas;
                        newState.valor_total = pagamentoCartao?.parcela.total;
                        newState.valor_original = Number(pagamentoCartao?.valor_original);
                        await getParcelas(true, pagamentoCartao.codigo_venda)
                        setParcelaState(pagamentoCartao.parcela)

                    }
                }
            }
            if (pagamentoValue <= 0 || !solo) {
                setPagamentoValue(Number(Number(pagamentoCartao?.valor_original).toFixed(2)))
            }
        })()
    }, [pagamentoCartao]);

    useEffect(() => {
        setConsultaParcela({
            codigo_venda: String(state.codigo_venda),
            valor: state.valor_original,
            bandeira: state.issuer,
            entrada: entrada,
        });
    }, []);

    const isValid = () => (state.is_valid && state.cvc !== "" && state.expiry !== "" && state.issuer !== "" && state.name !== "" && state.number !== "" && state.parcelas > 0)

    const getParcelas = async (e: any, codigo_venda?: string): Promise<void> => {
        const _codigoVenda = codigo_venda || state.codigo_venda || null
        let _pagamentoValue = pagamentoValue;
        
        if (valorReferencia && valorReferencia <= _pagamentoValue) {
            _pagamentoValue = valorReferencia;
            setPagamentoValue(_pagamentoValue)
        } 
        
        if (state.is_valid && _codigoVenda) {
            if (!consultaParcela) {
                return;
            }

            if (_pagamentoValue === -1) {
                return;
            }

            consultaParcela.valor = _pagamentoValue;
            const response = await pagamentoProvider.getParcelas(consultaParcela);
            if (response.statusCode === STATUS_CODE.OK && response.data) {
                setParcelasListState(response.data);
                if (entrada || (qtdMaxParcelas && qtdMaxParcelas === 1)) {
                    const parcela = (response.data as CartaoCreditoParcelaType[]).find((p) => p.parcelas === 1);
                    if (parcela) {
                        const newState: CartaoCreditoType & PagamentoCartaoType = { ...state };
                        newState.valor_parcela = parcela.valor;
                        newState.parcelas = parcela.parcelas;
                        newState.parcela = parcela;
                        newState.valor_total = parcela.total;
                        newState.valor_original = Number(pagamentoValue);
                        setDisableParcelas(true);
                        setParcelaState({ ...parcela });
                        onChange(newState);
                    }
                }
            }
        }
    }
    const maskName = useCallback((name: any)=>{
        let _name = name;
        _name = _name.replace(/[^a-zA-Z0-9 ]/g, '');
        return _name;
    },[]);    

    return <>
        <div className="CartaoCredito">
            <div className="cartao-credito-form">

                <div className="cartao-credito-container">
                    <Cards
                        cvc={state.cvc}
                        expiry={state.expiry.replace(/\D/g, "")}
                        focused={state.focus}
                        name={maskName(state.name)}
                        number={state.number}
                        locale={{ valid: "Validade" }}
                        placeholders={{ name: "Seu nome aqui" }}
                        callback={(issuer, is_valid) => {
                            const newState: CartaoCreditoType & PagamentoCartaoType = { ...state }
                            newState.is_valid = is_valid;
                            newState.issuer = issuer.issuer;
                            const _hasError: ErrorCartao = CollectionUtil.merge({}, hasError)

                            if (["amex","discover"].includes(issuer.issuer)) {
                                setCvvMask("credit_card_cvc_4")
                            } else {
                                setCvvMask("credit_card_cvc")
                            }

                            if (!is_valid) {
                                _hasError['numero'] ="Número do cartão inválido!"
                            } else {
                                delete _hasError['numero']
                            }
                            setErrors(_hasError)
                            onChange(newState);
                        }}
                    />
                </div>
                <div className="inputs-container">                    
                    <div className="row">
                        <div className="X12">
                            <InputMoney
                                label={"Valor pagamento"}
                                value={pagamentoValue}
                                defaultValue={pagamentoValue}                                
                                disabled={entrada || disabled || (!solo && disabledValoPargamento)}
                                onChangeValue={(e) => {
                                    let value = Number(e.numberValue);                                    
                                    const _state: any = { ...consultaParcela };

                                    _state.valor = value;

                                    const _newState: CartaoCreditoType & PagamentoCartaoType = { ...state }
                                    const newState: any = { ...state }
                                    newState.valor_original = _state.valor
                                    _newState.valor_original = _state.valor
                                    if (onChangeValue) {
                                        onChangeValue({
                                            newValue: newState,
                                            isValid: isValid()
                                        })
                                    }

                                    setState(_newState)
                                    setPagamentoValue(_state.valor)
                                    setConsultaParcela(_state)
                                }}
                                onBlur={async (e: any) => {
                                    getParcelas(e);
                                }}
                            />
                        </div>
                        <div className="X12">
                            <InputText
                                name="number"
                                type="tel"
                                fullWidth
                                autofocus={true}
                                label="Número do cartão"
                                onChange={handleInputChange}                                
                                onFocus={handleInputFocus}
                                mask="credit_card"
                                disabled={disabled}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                onBlur={async (e: any) => {
                                    await getParcelas(e);
                                }}
                                inputProps={{
                                    autocomplete: false,
                                    maxLength: 16
                                }}
                                value={state.number}
                                onKeyUp={(e: any) => {
                                    if (String(e.target.value) !== String(state.number)) {
                                        const newState = { ...state };
                                        newState.parcela = null;
                                        setParcelaState(null);
                                    }
                                }}
                                error={hasError('numero', errors).hasError}
                                helperText={hasError('numero', errors).hasError ? hasError('numero', errors).message : undefined}
                            />

                        </div>
                        <div className="X12">

                            <InputText
                                name="name"                                
                                fullWidth
                                label="Nome do titular do cartão"
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                disabled={disabled}
                                placeholder="Nome impresso no cartão"
                                value={maskName(state.name)}
                            />
                        </div>
                        <div className="X12">
                            <InputDateMonthYear
                                label="Validade"
                                fullWidth
                                placeholder={"Clique para selecionar"}
                                value={parseDate(state.expiry)}
                                minDate={getMinDate()}
                                maxDate={getMaxDate()}
                                name="expiry"
                                onChange={(newDate) => {
                                    const _state = { ...state }
                                    if (newDate) {
                                        const ano = newDate.slice(0, 4)
                                        const mes = newDate.slice(5, 7)
                                        _state.expiry = `${mes}/${ano}`
                                    } else {
                                        _state.expiry = ``
                                    }
                                    setState(_state)
                                }}
                                format="MM/yyyy"
                            />

                            {/* <InputExpiryDate
                                name="expiry"
                                onChangeValue={handleInputChange}
                                value={state.expiry}
                            /> */}
                        </div>
                        <div className="X12">
                            <InputText
                                name="cvc"
                                type="number"
                                fullWidth
                                label="CVV"
                                inputProps={{
                                    step: 1, max: 9999, min: 0
                                }}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onBlur={() => {
                                    setState({ ...state, focus: "number" })
                                }}
                                mask={cvvMask}
                                disabled={disabled}
                                value={state.cvc}
                            />
                        </div>


                        <div className="X12">
                            <Alert severity="warning">
                                <span style={{ fontSize: 12 }}>
                                    Parcelamentos acima de 12x estão sujeitos a análise/aprovação de crédito do banco Emissor do cartão.
                                </span>
                            </Alert>
                        </div>

                        <div className="X12">

                            <InputComboBoxList
                                label="Parcelas"
                                textField="label"
                                valueField="valor"
                                value={parcelaState}
                                options={parcelasListState.slice(0, qtdMaxParcelas ? qtdMaxParcelas : parcelasListState.length)}
                                disabled={disableParcelas || disabled || parcelasListState.slice(0, qtdMaxParcelas ? qtdMaxParcelas : parcelasListState.length).length === 0}
                                onChange={(e) => {
                                    const newState: CartaoCreditoType & PagamentoCartaoType = { ...state }
                                    if (e) {
                                        newState.parcelas = e.parcelas
                                        newState.valor_parcela = e.valor
                                        newState.parcela = e
                                    } else {
                                        newState.parcela = null;
                                    }
                                    const _isValid = isValid();
                                    if (!solo && onChangeValue) {
                                        onChangeValue({
                                            newValue: newState,
                                            isValid: _isValid
                                        })
                                    }

                                    setParcelaState(e)
                                    onChange(newState);
                                    setValid(_isValid);
                                }}
                            />

                        </div>
                    </div>

                </div>
                {solo && <div className="actions">
                    <FullWidthButton
                        text="Salvar"
                        disableElevation
                        variant="contained"
                        color="primary"
                        disabled={disabled || pagamentoCartao?.cvc === "" || (pagamentoCartao?.cvc as string)?.length < 3 || pagamentoCartao?.expiry === "" || pagamentoCartao?.name === "" || pagamentoCartao?.number === "" || pagamentoCartao?.parcela === null || pagamentoCartao?.issuer === "" || pagamentoCartao?.issuer === "unknow" || !pagamentoCartao?.is_valid}
                        onClick={() => {
                            onConfirmar();
                        }}

                    />
                </div>}
            </div>
        </div>
    </>
}

export { CartaoCredito }